import React from "react";
import { Box, Button, Typography } from "@mui/material";

// Shared class-hub shell — single source of the "class dashboard" look for
// student, instructor, and coordinator views. Visual spec mirrors the
// coordinator course detail page: breadcrumb → hero title + pill chips →
// icon stat tiles → underline tab bar with count badges.
const token = {
  ink: "var(--color-ink)",
  ink2: "rgba(31,22,51,0.75)",
  ink3: "rgba(31,22,51,0.6)",
  ink4: "rgba(31,22,51,0.5)",
  line: "var(--color-hairline-cloud)",
  surface: "var(--color-surface-canvas-light)",
  surface3: "var(--color-surface-press-stronger)",
  primary: "var(--color-primary)",
  live: "#c23d68",
  liveBg: "color-mix(in srgb, var(--color-accent-pink) 16%, transparent)",
  liveBorder: "color-mix(in srgb, var(--color-accent-pink) 30%, transparent)",
  shadowSm: "var(--shadow-card-sm)",
};

function Pill({ children, bg, color, border, mono }) {
  return (
    <Box component="span" sx={{
      display: "inline-flex", alignItems: "center", gap: "6px",
      fontSize: "12px", fontWeight: 700, px: "10px", py: "4px",
      borderRadius: "999px", letterSpacing: ".03em",
      bgcolor: bg || token.surface3,
      color: color || token.ink2,
      border: `1px solid ${border || token.line}`,
      ...(mono && { fontFamily: '"JetBrains Mono", monospace' }),
    }}>
      {children}
    </Box>
  );
}

function StatTile({ stat }) {
  return (
    <Box sx={{
      bgcolor: token.surface,
      border: `1px solid ${token.line}`,
      borderRadius: "14px",
      p: "16px 18px",
      boxShadow: token.shadowSm,
      display: "flex",
      alignItems: "center",
      gap: "14px",
      minWidth: 0,
    }}>
      {stat.icon && (
        <Box sx={{
          width: 38, height: 38, borderRadius: "10px", flexShrink: 0,
          display: "grid", placeItems: "center",
          bgcolor: stat.iconBg || "var(--color-primary-10)",
          color: stat.iconColor || token.primary,
        }}>
          {stat.icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: "26px", fontWeight: 800, letterSpacing: "-.04em", lineHeight: 1, color: stat.color || token.ink }}>
          {stat.value}
        </Box>
        <Box sx={{ fontSize: "12px", fontWeight: 600, color: token.ink3, mt: "2px" }}>
          {stat.label}
        </Box>
        {(stat.sub || stat.note) && (
          <Box sx={{ fontSize: "11.5px", fontWeight: 600, color: stat.subColor || token.ink4, mt: "2px" }}>
            {stat.sub || stat.note}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function TabBadge({ n, active, live }) {
  return (
    <Box component="span" sx={{
      fontSize: "11px", fontWeight: 800, px: "6px", py: "1px", borderRadius: "6px",
      bgcolor: live
        ? token.liveBg
        : active ? "var(--color-primary-10)" : token.surface3,
      color: live ? token.live : active ? token.primary : token.ink3,
      ...(live && {
        border: `1px solid ${token.liveBorder}`,
        boxShadow: "0 0 8px color-mix(in srgb, var(--color-accent-pink) 40%, transparent)",
      }),
      ml: "6px",
    }}>
      {n}
    </Box>
  );
}

export default function ClassOverview({
  breadcrumbLabel,
  onBreadcrumbClick,
  title,
  subtitle,
  status,
  banner,
  chips = [],
  stats = [],
  tabs = [],
  activeTab,
  onTabChange,
  actions = [],
  showContentFrame = true,
  children,
}) {
  return (
    <Box sx={{ maxWidth: 1240, mx: "auto", px: { xs: "16px", md: "40px" }, pt: { xs: 2.5, sm: 4 }, pb: showContentFrame ? { xs: 2.5, sm: 4 } : 0 }}>
      {banner}
      {breadcrumbLabel && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "14px", fontSize: "13px" }}>
          {onBreadcrumbClick ? (
            <Box component="span" onClick={onBreadcrumbClick}
              sx={{ color: token.primary, fontWeight: 600, cursor: "pointer", "&:hover": { opacity: 0.8 } }}>
              {breadcrumbLabel}
            </Box>
          ) : (
            <Box component="span" sx={{ color: token.ink3, fontWeight: 600 }}>{breadcrumbLabel}</Box>
          )}
          <Box component="span" sx={{ color: token.ink4 }}>/</Box>
          <Box component="span" sx={{ fontWeight: 700, color: token.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {title}
          </Box>
        </Box>
      )}

      {/* Hero row */}
      <Box sx={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        gap: "20px", mb: "18px", flexWrap: { xs: "wrap", sm: "nowrap" },
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="h1" sx={{
            m: 0, fontSize: { xs: "26px", sm: "32px" }, fontWeight: 600,
            letterSpacing: "-.01em", color: token.ink, lineHeight: 1.1,
            fontFamily: "var(--font-display)",
          }}>
            {title}
          </Typography>
          {(chips.length > 0 || status) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", mt: "9px" }}>
              {chips.map((chip) => (
                <Pill key={chip.label} bg={chip.bg} color={chip.color} border={chip.border} mono={chip.mono}>
                  {chip.icon}
                  {chip.label}
                </Pill>
              ))}
              {status && (
                <Pill
                  bg={status.background || "var(--color-primary-10)"}
                  color={status.color || token.primary}
                  border={status.border || `color-mix(in srgb, ${status.color || token.primary} 18%, transparent)`}
                >
                  {status.label}
                </Pill>
              )}
            </Box>
          )}
          {subtitle && <Typography sx={{ color: token.ink3, fontSize: 14, mt: "9px" }}>{subtitle}</Typography>}
        </Box>
        {actions.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flexShrink: 0 }}>
            {actions.map((action) => (
              <Button
                key={action.key}
                onClick={action.onClick}
                variant={action.variant || "contained"}
                disabled={action.disabled}
                sx={{ whiteSpace: "nowrap" }}
              >
                {action.label}
              </Button>
            ))}
          </Box>
        )}
      </Box>

      {/* Stat tiles */}
      {stats.length > 0 && (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: `repeat(${Math.min(stats.length, 4)}, 1fr)` },
          gap: "12px",
        }}>
          {stats.map((stat) => <StatTile key={stat.label} stat={stat} />)}
        </Box>
      )}

      {/* Underline tab bar — single-row horizontal scroll on narrow screens */}
      {tabs.length > 0 && (
        <Box
          role="tablist"
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            overflowX: "auto",
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "thin",
            borderBottom: `1px solid ${token.line}`,
            mt: "20px",
            mx: { xs: -0.5, sm: 0 },
            px: { xs: 0.5, sm: 0 },
          }}
        >
          {tabs.map((tab) => {
            const active = tab.key === activeTab;
            const isLive = Boolean(tab.live);
            return (
              <Box
                key={tab.key}
                component="button"
                role="tab"
                aria-selected={active}
                data-live={isLive ? "true" : undefined}
                onClick={() => onTabChange && onTabChange(tab.key)}
                sx={{
                  height: 44, px: { xs: "13px", sm: "18px" }, border: 0,
                  borderRadius: isLive ? "10px 10px 0 0" : 0,
                  background: isLive
                    ? "radial-gradient(ellipse at 50% 65%, color-mix(in srgb, var(--color-accent-pink) 20%, transparent) 0%, transparent 80%)"
                    : "transparent",
                  boxShadow: isLive
                    ? "inset 0 0 14px color-mix(in srgb, var(--color-accent-pink) 24%, transparent), 0 0 12px color-mix(in srgb, var(--color-accent-pink) 28%, transparent)"
                    : "none",
                  borderBottom: `2.5px solid ${active ? token.primary : isLive ? token.live : "transparent"}`,
                  fontFamily: "inherit", fontSize: "14px", fontWeight: 600,
                  color: active ? token.primary : isLive ? token.live : token.ink3, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: "8px",
                  mb: "-1px", flexShrink: 0, whiteSpace: "nowrap",
                  transition: "color .13s, border-color .13s, box-shadow .13s",
                  "&:hover": { color: active ? token.primary : isLive ? token.live : token.ink },
                }}
              >
                {tab.icon}
                {tab.label}
                {isLive && (
                  <Box
                    component="span"
                    aria-hidden="true"
                    data-testid={`tab-live-indicator-${tab.key}`}
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      bgcolor: token.live,
                      boxShadow: `0 0 8px ${token.live}`,
                      position: "relative",
                      flexShrink: 0,
                      "@keyframes class-overview-live-pulse": {
                        "0%": { transform: "scale(.6)", opacity: 0.55 },
                        "100%": { transform: "scale(2.1)", opacity: 0 },
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: -3,
                        borderRadius: "50%",
                        bgcolor: token.live,
                        opacity: 0.4,
                        animation: "class-overview-live-pulse 1.6s ease-out infinite",
                      },
                    }}
                  />
                )}
                {tab.count != null && <TabBadge n={tab.count} active={active} live={isLive} />}
              </Box>
            );
          })}
        </Box>
      )}

      {showContentFrame ? (
        <Box sx={{ pt: "24px", pb: "60px" }}>{children}</Box>
      ) : children}
    </Box>
  );
}
